import * as Api from '@ViewModels';
import moment from 'moment';
import { ISelectBoxOption } from '../../web/components/SelectBox';
import { ICreateCampaignRequest, IKeyDateSelection, TemplateOrTemplateFilter } from '../index';
import { getDefaultTimeStringValue } from './dateUtils';

/**
 * Calculates schedule campaign calendar date range
 * @source Original location: UiUtils.ts.backup line 114
 */
export const calcScheduleCampaignCalendarDateRange = ({
	selectedDate = new Date(),
	numWeeks = 4,
}: {
	selectedDate?: Date | moment.Moment;
	numWeeks?: number;
} = {}) => {
	const calendarStartDate = moment(selectedDate).startOf('month').startOf('week');
	const calendarEndDay = moment(calendarStartDate)
		.add(numWeeks - 1, 'weeks')
		.endOf('week');
	return { startDate: calendarStartDate.toDate(), endDate: calendarEndDay.toDate() };
};

/**
 * Gets schedule time options
 *
 * Returns options where `value` is total minutes from midnight (e.g. 540 = 9:00am, 555 = 9:15am).
 *
 * @param minutes - Deprecated. Always ignored — retained for backwards compatibility. Pass 0.
 * @param incrementMinutes - Time increment in minutes (default 60 for hourly, use 15 for quarter-hour)
 * @param enforceNextHourMinimum - When true and selectedMoment is today, the earliest option is the top
 *   of the next hour. This ensures the backend hourly cron job runs before the scheduled time.
 * @source Original location: UiUtils.ts.backup line 1283
 */
export const getScheduleTimeOptions = (
	minutes: number,
	userSession?: Api.UserSessionContext,
	sendEmailFeatures?: Partial<Api.ISendEmailFeatures>,
	selectedMoment?: moment.Moment,
	impersonationContext?: Api.IImpersonationContext,
	ignoreEmailIntervals = false,
	incrementMinutes = 60,
	enforceNextHourMinimum = false
) => {
	let interval: Api.IInterval = null;
	const accountFeatures =
		impersonationContext?.account?.features?.sendEmail || userSession?.account?.features?.sendEmail;
	const sendEmail = sendEmailFeatures || accountFeatures;

	const checkMoment = moment(selectedMoment) || moment();

	if (ignoreEmailIntervals || !sendEmail?.observeSendIntervals) {
		interval = { endMinutes: 1440, startMinutes: 0 };
	} else {
		// isoWeekday returns 1-7 where 1 is Monday and 7 is Sunday
		switch (checkMoment.isoWeekday()) {
			case 1:
				interval = sendEmail?.useDiscreteIntervals ? sendEmail?.mondayInterval : sendEmail?.weekdaysInterval;
				break;
			case 2:
				interval = sendEmail?.useDiscreteIntervals ? sendEmail?.tuesdayInterval : sendEmail?.weekdaysInterval;
				break;
			case 3:
				interval = sendEmail?.useDiscreteIntervals ? sendEmail?.wednesdayInterval : sendEmail?.weekdaysInterval;
				break;
			case 4:
				interval = sendEmail?.useDiscreteIntervals ? sendEmail?.thursdayInterval : sendEmail?.weekdaysInterval;
				break;
			case 5:
				interval = sendEmail?.fridayInterval;
				break;
			case 6:
				interval = sendEmail?.saturdayInterval;
				break;
			case 7:
				interval = sendEmail?.sundayInterval;
				break;
			default:
				break;
		}
	}

	const ret: ISelectBoxOption<number>[] = [];
	if (interval) {
		// only replace if null or undefined - leave 0 intact
		interval.startMinutes = interval.startMinutes ?? 0;
		interval.endMinutes = interval.endMinutes ?? 1440;
		let startMin = Math.ceil(interval.startMinutes / incrementMinutes) * incrementMinutes;

		// Backend dispatches scheduled emails via an hourly cron (:00). When scheduling for today
		// with sub-hour increments, the earliest safe slot is the top of the next hour so the cron
		// always runs between "now" and the scheduled time.
		if (enforceNextHourMinimum && selectedMoment?.isSame(moment(), 'day')) {
			const nextTopOfHour = (moment().hours() + 1) * 60;
			startMin = Math.max(startMin, nextTopOfHour);
		}
		// Back off by one increment so the last offered slot leaves a full increment before business
		// hours end. With hourly increments and a 6:00pm end, the last slot is 5:00pm; with 15-minute
		// increments and a 6:00pm end, the last slot is 5:45pm.
		const endMin = interval.endMinutes - incrementMinutes;
		const selectedDate = new Date();

		for (let totalMin = startMin; totalMin <= endMin; totalMin += incrementMinutes) {
			const h = Math.floor(totalMin / 60);
			const m = totalMin % 60;
			selectedDate.setHours(h, m, 0);
			const title = getDefaultTimeStringValue(selectedDate);
			ret.push({
				isDefault: false,
				title,
				value: totalMin,
			});
		}
	}

	return ret;
};

/**
 * Checks if schedule send overlaps with existing workload
 * @source Original location: UiUtils.ts.backup line 1354
 */
export const scheduleSendOverlapsWithExistingWorkload = (
	estimate?: Partial<Api.IEmailSendEstimate>,
	selectedDate?: Date,
	numberOfEmailsToSend?: number
) => {
	return !!selectedDate && !!estimate && !!estimate.emailWorkload
		? !!estimate.emailWorkload.forecast.find(x => {
				const sendStartMoment = moment(estimate.estimate.startDate).startOf('day');
				const sendEndMoment = moment(estimate.estimate.endDate).endOf('day');
				const numberOfDays = sendEndMoment.diff(sendStartMoment, 'days');
				const dateOverlaps = moment(x.date).startOf('day').isBetween(sendStartMoment, sendEndMoment, null, '[]');
				if (dateOverlaps) {
					if (numberOfDays > 0) {
						// send spans more than one 24hr period
						return true;
					}
					const dailyLimit =
						(estimate.emailWorkload.limits.find(limit => limit.intervalInMinutes === 1440) || {}).count || 0;
					return dailyLimit - x.count - (numberOfEmailsToSend || 1) < 0;
				}
				return false;
			})
		: false;
};

/**
 * Gets interval time options
 * @source Original location: UiUtils.ts.backup line 1465
 */
export const getIntervalTimeOptions = () => {
	const ret: ISelectBoxOption<number>[] = [];

	const selectedDate = new Date();
	for (let x = 0; x <= 23; x++) {
		for (let y = 0; y <= 3; y++) {
			const minutes = y * 15;
			selectedDate.setHours(x, minutes, 0);
			ret.push({
				title: getDefaultTimeStringValue(selectedDate),
				value: x * 60 + minutes,
			});
		}
	}

	return ret;
};

export const createKeyDateSelection = (
	keyDate: Api.IKeyDate,
	template?: TemplateOrTemplateFilter
): IKeyDateSelection => {
	return {
		contactSelection: {
			bulkFilterRequest: {
				contactFilterRequest: {
					criteria: [
						{
							property: Api.ContactFilterCriteriaProperty.UpcomingKeyDatesNotScheduled,
							value: keyDate?.kind,
						},
					],
				},
			},
		},
		kind: keyDate?.kind,
		month: keyDate?.month,
		template,
	};
};

export const getCreateCampaignRequestForKeyDate = (
	keyFact: Api.IKeyFact,
	template?: Api.ITemplate,
	request?: Omit<ICreateCampaignRequest, 'context' | 'type'>
): ICreateCampaignRequest<IKeyDateSelection> => {
	return {
		...(request || {}),
		context: createKeyDateSelection(keyFact?.keyDate, template),
		type: 'KeyDate',
	};
};

export const isCampaignScheduledToSend = (campaign: Api.ICampaign) => {
	return !campaign.cancelledDate && !campaign.completedDate && !!campaign.schedule;
};

export const hasCampaignStarted = (campaign: Api.ICampaign) =>
	isCampaignScheduledToSend(campaign) ? new Date(campaign.schedule.startDate) < new Date() : false;

export const campaignQueuedCount = (campaign: Api.ICampaign) =>
	campaign.totalEmails - campaign.sentSuccessfullyCount - campaign.failedCount - (campaign.noEmailAddressCount ?? 0);

export const isCampaignInFinalStatus = (campaign: Api.ICampaign) =>
	campaign.status === Api.EmailSendStatus.Complete ||
	campaign.status === Api.EmailSendStatus.Cancelled ||
	campaign.status === Api.EmailSendStatus.Rejected;

export const formatDateRangeString = (value?: Api.IRange<Date | null>): string => {
	if (!value?.start) {
		return '';
	}

	const f = 'MM/DD/YYYY';
	return `${moment(value.start).format(f)} - ${moment(value.end).format(f)}`;
};

export const createDraggableId = (stageId: string, opportunityId: string) => {
	return `${stageId}|${opportunityId}`;
};

export const parseDraggableId = (draggableId: string) => {
	const [stageId, opportunityId] = draggableId.split('|');
	return { stageId, opportunityId };
};

export interface ICampaignSendMethod {
	type: 'immediate' | 'explicit-spread' | 'implicit-spread';
	durationInDays?: number;
}

/**
 * Determines the send method for a campaign based on its schedule and categories.
 * Distinguishes between:
 * - Immediate sends (≤1 day)
 * - Explicit spread (user chose to spread over multiple days via SpreadOverDays category)
 * - Implicit spread (multi-day due to daily send limits, not user choice)
 *
 * @param campaign - The campaign to analyze
 * @returns An object indicating the send type and duration
 */
export const getCampaignSendMethod = (campaign?: Api.ICampaign): ICampaignSendMethod | null => {
	if (!campaign) {
		return null;
	}

	if (!campaign.schedule) {
		return null;
	}

	// Calculate the duration from start and end dates
	const startDate = campaign.schedule.startDate;
	const endDate = campaign.schedule.endDate;

	if (!startDate || !endDate) {
		return null;
	}

	const startMoment = moment(startDate).startOf('day');
	const endMoment = moment(endDate).startOf('day');
	// Add 1 to count inclusive calendar days (start day + end day)
	const durationInDays = endMoment.diff(startMoment, 'days') + 1;

	/**
	 * Business Logic: A campaign is considered "immediate" if the duration is <= 2 calendar days.
	 * Examples:
	 * - Same day (Day 1 to Day 1): diff = 0, +1 = 1 calendar day → immediate
	 * - Adjacent days (Day 1 to Day 2): diff = 1, +1 = 2 calendar days → immediate (sent quickly)
	 * - Multi-day (Day 1 to Day 3+): diff >= 2, +1 >= 3 calendar days → spread over multiple days
	 */
	if (durationInDays <= 2) {
		return { type: 'immediate' };
	}

	// Check if the campaign has the SpreadOverDays category
	// Note: SpreadOverDays is not in the TypeScript enum, but can exist as a string value from the backend
	const hasSpreadOverDaysCategory = campaign.categories?.some(
		category => category === ('SpreadOverDays' as Api.EmailCategories)
	);

	if (hasSpreadOverDaysCategory) {
		// User explicitly chose to spread over multiple days
		return {
			type: 'explicit-spread',
			durationInDays,
		};
	}

	// Multi-day campaign without explicit spread - due to daily send limits
	return {
		type: 'implicit-spread',
		durationInDays,
	};
};

/**
 * Formats the campaign send method as a human-readable string.
 * Displays different text based on whether the spread was explicit or due to limits.
 *
 * @param campaign - The campaign to format
 * @returns A formatted string describing how the campaign was sent
 */
export const formatCampaignSendMethod = (campaign?: Api.ICampaign): string | null => {
	const sendMethod = getCampaignSendMethod(campaign);

	if (!sendMethod) {
		return null;
	}

	if (sendMethod.type === 'immediate') {
		return 'As quickly as possible';
	}

	if (sendMethod.type === 'explicit-spread') {
		return `Spread over ${sendMethod.durationInDays} days`;
	}

	// implicit-spread
	return `${sendMethod.durationInDays} days (due to daily send limits)`;
};
